import { Button, Group, SimpleGrid, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { DEFAULT_FORMAT, formatById, NewRoundInputSchema, newRoundLabels, type NewRoundInput, type AppError } from '@flow/core';
import { applyErrors, zodValidate } from '../../ui/forms';
import { FormatSelect } from '../../ui/FormatSelect';
import { reportError } from '../../app/services';

export interface RoundFormValues {
  label: string;
  room: string;
  flight: string;
  startTime: string;
  teamA: { code: string; school: string };
  teamB: { code: string; school: string };
  teamANames: string[];
  teamBNames: string[];
  formatId: string;
  resolution: string;
}

export const emptyRoundForm = (label = 'R1', formatId = DEFAULT_FORMAT.id, resolution = ''): RoundFormValues => ({
  label, room: '', flight: '', startTime: '',
  teamA: { code: '', school: '' }, teamB: { code: '', school: '' },
  teamANames: ['', ''], teamBNames: ['', ''],
  formatId,
  resolution,
});

/** Quick add / edit round. All errors show at once, in plain language, on the right fields. */
export const RoundForm = ({ initial, submitLabel, onSubmit, onCancel, suggestResolution }: {
  initial: RoundFormValues;
  submitLabel: string;
  /** Prefill for a format's resolution (the tournament's last one for that event). */
  suggestResolution?: (formatId: string) => string;
  onSubmit: (v: NewRoundInput, formatId: string) => Promise<AppError | null>;
  onCancel?: () => void;
}) => {
  const form = useForm<RoundFormValues>({
    mode: 'controlled',
    initialValues: initial,
    validate: zodValidate(NewRoundInputSchema, newRoundLabels),
    validateInputOnBlur: true,
  });

  const submit = form.onSubmit(async (values) => {
    const size = formatById(values.formatId)?.teamSize ?? 2;
    const e = await onSubmit({ ...values, teamANames: values.teamANames.slice(0, size), teamBNames: values.teamBNames.slice(0, size) }, values.formatId);
    // Field problems go on the fields; anything else (e.g. "this round already has notes") becomes a notice.
    if (e && !applyErrors(form, e)) reportError(e);
  });

  const format = formatById(form.values.formatId) ?? DEFAULT_FORMAT;
  const teamSize = format.teamSize;
  const team = (key: 'teamA' | 'teamB', namesKey: 'teamANames' | 'teamBNames', title: string) => (
    <Stack gap={6}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">{title}</Text>
      <TextInput label="Team code" placeholder="e.g. Lincoln AB" required {...form.getInputProps(`${key}.code`)} data-testid={`${key}-code`} />
      <TextInput label="School" placeholder="optional" {...form.getInputProps(`${key}.school`)} />
      <SimpleGrid cols={teamSize === 1 ? 1 : 2} spacing={6}>
        {Array.from({ length: teamSize }, (_, i) => i).map((i) => (
          <TextInput key={i} label={teamSize === 1 ? 'Debater' : `Speaker ${i + 1}`} placeholder="name (this device only)" {...form.getInputProps(`${namesKey}.${i}`)} />
        ))}
      </SimpleGrid>
    </Stack>
  );

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="md">
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
          <TextInput label="Round" required {...form.getInputProps('label')} />
          <TextInput label="Room" {...form.getInputProps('room')} />
          <TextInput label="Flight" placeholder="A / B" {...form.getInputProps('flight')} />
          <TextInput label="Start" placeholder="09:30" {...form.getInputProps('startTime')} />
        </SimpleGrid>
        <FormatSelect
          value={form.values.formatId}
          onChange={(id) => {
            // Swap in that event's resolution unless the judge typed their own.
            const cur = form.values.resolution.trim();
            if (suggestResolution && (!cur || cur === suggestResolution(form.values.formatId).trim())) form.setFieldValue('resolution', suggestResolution(id));
            form.setFieldValue('formatId', id);
          }}
          testId="round-format"
        />
        <Textarea
          label="Resolution"
          placeholder={format.event === 'parli' ? 'Announced before each round. Add it now or on the round page.' : 'Resolved: … (optional)'}
          autosize
          minRows={1}
          maxRows={4}
          {...form.getInputProps('resolution')}
          data-testid="round-resolution"
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          {team('teamA', 'teamANames', teamSize === 1 ? 'Debater A' : 'Team A')}
          {team('teamB', 'teamBNames', teamSize === 1 ? 'Debater B' : 'Team B')}
        </SimpleGrid>
        <Text size="xs" c="dimmed">
          {format.order === 'coinFlip' ? 'Sides and speaking order come from the coin flip.' : `Sides (${format.sideLabels.join(' / ')}) are usually on the pairing.`} Set them later.
          Debater names stay on this device and are deleted 72h after you submit the ballot.
        </Text>
        <Group justify="flex-end">
          {onCancel && <Button variant="subtle" onClick={onCancel}>Cancel</Button>}
          <Button type="submit" variant="filled">{submitLabel}</Button>
        </Group>
      </Stack>
    </form>
  );
};
